import { background } from "@scaleaq/sui-tokens/colors/background";
import { icon } from "@scaleaq/sui-tokens/colors/icon";
import { stroke } from "@scaleaq/sui-tokens/colors/stroke";
import { surface } from "@scaleaq/sui-tokens/colors/surface";
import { text } from "@scaleaq/sui-tokens/colors/text";
import { font, radius } from "@scaleaq/sui-tokens/responsive";
import { type ThemeDefaultParams, themeQuartz } from "ag-grid-enterprise";

// TODO: Currently copied from sui-react, this should be added to sui-tokens at some point.
const elevation1 = `0px 1px 4px rgba(0, 0, 0, 0.15)`;
const elevation2 = `0px 1px 6px rgba(0, 0, 0, 0.25), 0px 6px 8px rgba(0, 0, 0, 0.13)`;

export const themeParams: Partial<ThemeDefaultParams> = {
	accentColor: background.action.solid[100],
	advancedFilterBuilderButtonBarBorder: {
		color: stroke.neutral[50],
		style: "solid",
		width: 1,
	},
	advancedFilterBuilderColumnPillColor: background.neutral.soft[100],
	advancedFilterBuilderIndentSize: "1rem",
	advancedFilterBuilderJoinPillColor: background.neutral.soft[200],
	advancedFilterBuilderOptionPillColor: background.neutral.soft[300],
	advancedFilterBuilderValuePillColor: background.neutral.soft[300],
	backgroundColor: surface.cards,
	borderColor: stroke.neutral[50],
	borderRadius: radius.small,
	browserColorScheme: "inherit",
	buttonFontWeight: "900",
	cardShadow: elevation1,
	cellEditingBorder: false,
	cellEditingShadow: `0 0 0 -1px ${stroke.action.solid[100]}`,
	cellFontFamily: font.family.body,
	cellHorizontalPadding: "0.75rem",
	cellHorizontalPaddingScale: 1,
	cellTextColor: text.neutral.contrast,
	cellWidgetSpacing: "0.25rem",
	chartMenuLabelColor: text.neutral.contrast,
	chartMenuPanelWidth: "18.75rem",
	checkboxBorderRadius: radius.xsmall,
	checkboxBorderWidth: 1,
	checkboxCheckedBackgroundColor: background.action.solid[100],
	checkboxCheckedBorderColor: background.action.solid[100],
	checkboxCheckedShapeColor: icon.action.on.solid[100],
	checkboxCheckedShapeImage: undefined,
	checkboxIndeterminateBackgroundColor: background.action.solid[100],
	checkboxIndeterminateBorderColor: background.action.solid[100],
	checkboxIndeterminateShapeColor: icon.action.on.solid[100],
	checkboxIndeterminateShapeImage: undefined,
	checkboxUncheckedBackgroundColor: surface.cards,
	checkboxUncheckedBorderColor: stroke.neutral[50],
	chromeBackgroundColor: {
		ref: "backgroundColor",
	},
	columnBorder: false,
	columnDropCellBackgroundColor: background.neutral.soft[100],
	columnDropCellBorder: {
		color: stroke.neutral[200],
		style: "solid",
		width: 1,
	},
	columnDropCellDragHandleColor: icon.neutral.contrast,
	columnDropCellTextColor: text.neutral.contrast,
	columnHoverColor: background.neutral.soft[200],
	columnSelectIndentSize: "1.25rem",
	dataFontSize: font.size.medium,
	dialogBorder: {
		color: stroke.neutral[50],
		style: "solid",
		width: 1,
	},
	dialogShadow: elevation2,
	dragAndDropImageBackgroundColor: background[100],
	dragAndDropImageBorder: {
		color: stroke.neutral[200],
		style: "dotted",
		width: 1,
	},
	dragAndDropImageShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
	dragHandleColor: icon.neutral.contrast,
	dropdownShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
	filterToolPanelGroupIndent: "1rem",
	focusShadow: `0 0 0 0px ${stroke.focused}`,
	fontFamily: { googleFont: font.family.body },
	fontSize: font.size.medium,
	footerRowBorder: true,
	foregroundColor: text.neutral.contrast,
	headerBackgroundColor: surface.cards,
	headerCellBackgroundTransitionDuration: "0.3s",
	headerCellMovingBackgroundColor: background.neutral.soft[100],
	headerColumnBorder: false,
	headerColumnBorderHeight: 1,
	headerColumnResizeHandleColor: stroke.neutral[50],
	headerColumnResizeHandleHeight: "25%",
	headerColumnResizeHandleWidth: "0.125rem",
	headerFontFamily: { googleFont: font.family.body },
	headerFontSize: font.size.medium,
	headerFontWeight: font.weight.medium,
	headerHeight: "3rem",
	headerRowBorder: true,
	headerTextColor: text.neutral.contrast,
	headerVerticalPaddingScale: 1,
	iconButtonBackgroundSpread: "0.25rem",
	iconButtonBorderRadius: "1px",
	iconButtonHoverBackgroundColor: background.action.solid[100],
	iconButtonHoverColor: icon.action.on.solid[100],
	iconSize: "1.25rem",

	inputIconColor: icon.neutral.contrast,
	inputPaddingStart: "0.6875rem",
	inputTextColor: text.neutral.contrast,
	invalidColor: text.critical[100],
	listItemHeight: "2.25rem",
	menuBackgroundColor: surface.overlay,
	menuBorder: {
		color: stroke.neutral[50],
		style: "solid",
		width: 1,
	},
	menuSeparatorColor: stroke.neutral[50],
	menuShadow: elevation1,
	menuTextColor: text.neutral.contrast,
	panelBackgroundColor: surface.overlay,
	panelTitleBarBackgroundColor: surface.overlay,
	panelTitleBarBorder: {
		color: stroke.neutral[50],
		style: "solid",
		width: 1,
	},
	panelTitleBarFontWeight: font.weight.semi.bold,
	panelTitleBarIconColor: icon.neutral.contrast,
	panelTitleBarTextColor: text.neutral.contrast,
	pinnedColumnBorder: false,
	pinnedRowBorder: true,
	popupShadow: `0 0.25rem 0.5rem ${stroke.neutral[200]}`,
	rangeHeaderHighlightColor: background.action.soft[200],
	rangeSelectionBackgroundColor: background.action.soft[100],
	rangeSelectionBorderColor: stroke.action[100],
	rangeSelectionBorderStyle: "solid",
	rangeSelectionChartBackgroundColor: background.action.soft[100],
	rangeSelectionChartCategoryBackgroundColor: background.action.soft[100],
	rangeSelectionHighlightColor: background.action.soft[200],
	rowBorder: true,
	rowGroupIndentSize: "1rem",
	rowHeight: "3rem",
	rowHoverColor: background.action.soft[100],
	rowLoadingSkeletonEffectColor: background.transparent[100],
	rowNumbersSelectedColor: background.action.soft[100],
	rowVerticalPaddingScale: 1,
	selectCellBackgroundColor: background.action.soft[100],
	selectCellBorder: {
		color: stroke.neutral[200],
		style: "solid",
		width: 1,
	},
	selectedRowBackgroundColor: background.action.soft[100],
	setFilterIndentSize: "1rem",
	sideBarBackgroundColor: surface.cards,
	sideBarPanelWidth: "18.75rem",
	sideButtonBackgroundColor: surface.cards,
	sideButtonBarBackgroundColor: surface.cards,
	sideButtonBarTopPadding: "0.5rem",
	sideButtonBorder: {
		color: "transparent",
		style: "solid",
		width: 1,
	},
	sideButtonHoverBackgroundColor: background.action.soft[100],
	sideButtonHoverTextColor: text.neutral.contrast,
	sideButtonLeftPadding: "0.5rem",
	sideButtonRightPadding: "0.5rem",
	sideButtonSelectedBackgroundColor: background.action.soft[300],
	sideButtonSelectedBorder: {
		color: stroke.neutral[50],
		style: "solid",
		width: 1,
	},
	sideButtonSelectedTextColor: text.neutral.contrast,
	sideButtonSelectedUnderlineColor: stroke.action.soft[300],
	sideButtonSelectedUnderlineTransitionDuration: "0",
	sideButtonSelectedUnderlineWidth: 0,
	sideButtonTextColor: text.neutral.contrast,
	sideButtonVerticalPadding: "0.5rem",
	sidePanelBorder: true,
	spacing: "0.5rem",
	statusBarLabelColor: text.neutral.contrast,
	statusBarLabelFontWeight: font.weight.semi.bold,
	statusBarValueColor: text.neutral.contrast,
	statusBarValueFontWeight: font.weight.semi.bold,
	subtleTextColor: background.neutral.solid[100],
	tabSelectedTextColor: text.neutral.contrast,
	tabTextColor: text.neutral.contrast,
	textColor: text.neutral.contrast,
	toggleButtonHeight: "1.25rem",
	toggleButtonOffBackgroundColor: background.neutral.solid[100],
	toggleButtonOnBackgroundColor: background.action.solid[100],
	toggleButtonSwitchBackgroundColor: background[100],
	toggleButtonSwitchInset: "0.125rem",
	toggleButtonWidth: "2.5rem",
	toolPanelSeparatorBorder: {
		color: stroke.neutral[50],
		style: "solid",
		width: 1,
	},
	tooltipBackgroundColor: surface.cards,
	tooltipBorder: {
		color: stroke.neutral[50],
		style: "solid",
		width: 1,
	},
	tooltipTextColor: text.neutral.contrast,
	valueChangeDeltaDownColor: text.critical[100],
	valueChangeDeltaUpColor: text.success[100],
	valueChangeValueHighlightBackgroundColor: "rgba(0, 255, 0, 0.1)",
	widgetContainerHorizontalPadding: "1.25rem",
	widgetContainerVerticalPadding: "0.5rem",
	widgetHorizontalSpacing: "0.5rem",
	widgetVerticalSpacing: "0.5rem",
	wrapperBorder: {
		color: stroke.neutral[50],
		style: "solid",
		width: 1,
	},
	wrapperBorderRadius: radius.medium,
} as const;

export const noBorderParams: Partial<ThemeDefaultParams> = {
	wrapperBorder: false,
} as const;

export const compactParams: Partial<ThemeDefaultParams> = {
	headerHeight: "2.25rem",
	rowHeight: "2.25rem",
} as const;

const baseTheme = themeQuartz.withParams(themeParams);
export const theme = baseTheme;
export const noBorderTheme = baseTheme.withParams(noBorderParams);
export const compactTheme = baseTheme.withParams(compactParams);
